import { Tooltip, Typography, type TooltipProps, type TypographyProps } from '@mui/material';
import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';

interface Props extends TypographyProps {
  // `children` sale del Typography, nunca del caller: omitirlo es lo que hace usable esta prop —
  // `Omit<TooltipProps, 'title'>` seguía exigiéndolo y no había forma de pasar solo un título.
  tooltipProps?: Omit<TooltipProps, 'title' | 'children'> & {
    title?: ReactNode;
  };
}

const TruncatedTypography: FC<Props> = ({ children, tooltipProps, ...typographyProps }) => {
  const textRef = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const resizeTimer = useRef<NodeJS.Timeout>();

  const checkTruncation = () => {
    if (textRef.current) {
      // console.log('SCROLL WIDTH', textRef.current.scrollWidth);
      // console.log('CLIENT WIDTH', textRef.current.clientWidth);
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  useEffect(() => {
    checkTruncation();

    // apply debouncing
    const handleResize = () => {
      clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(() => {
        checkTruncation();
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [children]);

  // Un `tooltipProps.title` explícito es un tooltip deliberado y se muestra siempre: no depende de que
  // el texto se corte. Solo cuando no hay título propio el tooltip actúa como fallback de truncado y
  // repite el contenido. Sin esta distinción, un caller que quiera explicar la celda tiene que envolver
  // los children en su propio Tooltip — y termina con dos tooltips encimados.
  const title = tooltipProps?.title ?? (isTruncated ? children : '');

  return (
    <Tooltip {...tooltipProps} title={title}>
      <Typography ref={textRef} noWrap {...typographyProps}>
        {children}
      </Typography>
    </Tooltip>
  );
};

export default TruncatedTypography;
